import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import utc from 'dayjs/plugin/utc';
import strings from '../Globalization';
import consts from '../consts';
import util from '../util';
import logger from './logger';

dayjs.extend(utc);
const singleSort = true;
const operatorMapping = new Map([
	['equals', '$eq'],
	['is', '$eq'],
	['=', '$eq'],
	['>', '$gt'],
	['>=', '$gte'],
	['<', '$lt'],
	['<=', '$lte'],
	['!=', '$ne'],
	['isEmpty', '$notExist'],
	['isNotEmpty', '$isExist'],
	['not', '$not'],
	['isNot', '$not'],
	['onOrBefore', '$lte'],
	['after', '$gt'],
	['before', '$lt'],
	['onOrAfter', '$gte'],
	['isAnyOf', '$in'],
	['contains', 'like'],
	['startsWith', '$sw'],
	['endsWith', '$ew']
]);

const exportFileExtensions = new Map([["CSV", ".csv"], ["XLSX", ".xlsx"], ["PDF", ".pdf"]])

const evalAdjacentPayload = ({ gridColumns, filterModel, standaloneFilters, standaloneFilterColumns, parentFilters }) => {
	let params = {};
	let filterModelfilters = [];
	if (filterModel?.items?.length) {
		filterModelfilters = filterModel.items?.reduce(
			(acc, { operatorValue, value, field, columnField, operator, ...rest }) => {
				const fieldName = field || columnField;
				const op = operator || operatorValue;
				let filter = {
					operator: operatorMapping.get(operatorMapping.has(op) ? op : '='),
					property: fieldName,
					value:
						(op === 'contains' || op === 'startsWith' || op === 'endsWith') && typeof value === 'string'
							? `%${value}`
							: value
				};
				const column = gridColumns.find((gridColumn) => gridColumn.field === fieldName);
				const type = column?.filterType || column?.type;
				if (['date', 'datetime'].includes(type) && !column?.strictFilter && !!value) {
					const dayjsVal = dayjs(value);
					filter = { ...filter, type, convert: true };
					if (dayjsVal.isValid()) {
						if (type === 'date') {
							filter.value = dayjsVal.local().format(consts.MomentFormat.MonthDateYear);
						}
						filter.utcDate = dayjsVal.local().utc().toDate().toString();
					}
				}
				if (column && (!('type' in column) || !column.type|| column.type === 'string')) {
					filter.type = 'string';
				}

				acc.push(filter);
				return acc;
			},
			[]
		);
	}
	standaloneFilters = standaloneFilters ?? {};
	for (let [key, value] of Object.entries(standaloneFilters)) {
		let column = standaloneFilterColumns
			.concat(gridColumns)
			.find((col) => col.field === key || col.headerFilterField === key);
		if (column?.filterParams) {
			const filterParams =
				typeof column?.filterParams === 'function' ? column?.filterParams(value) : column?.filterParams;
			filterModelfilters.push({
				operator: 'eq',
				property: key,
				...filterParams,
				value: value
			});
		} else {
			params[key] = value;
		}
	}


	const { filter: parentFiltersFilter = [], ...otherParentFilters } = { ...parentFilters };
	if (Array.isArray(parentFiltersFilter) && filterModelfilters?.length > 0) {
		const parentFiltersFilterKeys = parentFiltersFilter.map(({ property }) => property);
		filterModelfilters = filterModelfilters.filter(({ property }) => !parentFiltersFilterKeys.includes(property));
	}
	const adjacentPayload = { ...params, filter: [...filterModelfilters, ...parentFiltersFilter], ...otherParentFilters };
	for (const item in adjacentPayload) {
		if (dayjs.isDayjs(adjacentPayload[item])) {
			adjacentPayload[item] = adjacentPayload[item].toISOString();			
		}
	}
	return adjacentPayload;
};
export const onError = (err) => {
	const { info, err: errInfo, icon = 'error', infoMessage, ...rest } = err;
	const text = info ? (strings[info] || info) : errInfo ? (strings[errInfo] || errInfo) : strings.CheckNetwork;
	Swal.fire({ titleText: strings['SaveFailed'] || "Save Failed", html: infoMessage || text, icon: icon, confirmButtonText: strings.Ok, allowOutsideClick: false });
};

const onSuccess = ({ info }) => {
	let text = typeof info === 'string' ? strings[info] : strings.SaveSuccessText;
	Swal.fire({ titleText: strings.SavedData, text: text, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
};

export const exportList = async ({
	gridColumns,
	standaloneFilterColumns,
	sortModel,
	filterModel,
	api,
	setIsLoading,
	parentFilters,
	standaloneFilters,
	apiParams,
	exportFormat,
	modelConfig,
	hiddenColumns,
	title,
	exportConfig,
	limit
}) => {
	const lookups = {};
	const dateFields = [];
	const cols = [];
	const filters = [];
	setIsLoading(true);
	gridColumns.forEach(({ lookup, isLocal, field, type, hide, headerName, width, pdfExportWidth, exportCharLimit }) => {
		if (hiddenColumns?.length === 0 || !(hiddenColumns?.length > 0 && hiddenColumns.includes(field))) {
			let exportCol = {
				ColumnName: field,
				Header: headerName,
				...(type === 'date' || type === 'dateTime' || type === 'time'
					? { Convert: true, type: 'date', isLocal: Boolean(isLocal) }
					: {})
			};
			if (exportFormat === 'PDF' && typeof pdfExportWidth === 'number') {
				exportCol.pdfExportWidth = pdfExportWidth;
			}
			if (exportCharLimit) {
				exportCol.exportCharLimit = exportCharLimit
			}
			cols.push(exportCol);
		}
		if (type === 'date') {
			dateFields.push(field);
			return;
		}
		if (!lookup) {
			return;
		}
	});
	const formData = new FormData();
	formData.append('exportFormat', exportFormat);
	formData.append('v', new Date());
	let fileName = `${typeof strings[title] === 'string' ? strings[title] : title}`;
	if (title === consts.commandHistory) {
		const timestamp = dayjs().format(consts.MomentFormat.TimeStampFormat);
		fileName = `${strings.CommandHistory}_${util.localData.loginData.tags.HospitalId}_${timestamp}`
	}
	formData.append('title', fileName);
	if (apiParams) {
		Object.keys(apiParams).forEach((key) => {
			formData.append(key, apiParams[key]);
		});
	}
	if (sortModel?.length) {
		const sort = sortModel.map((sort) => {
			return { sort: sort.field, dir: sort.sort };
		});

		formData.append('sortBy', sort[0].sort);
		formData.append('sortDir', sort[0].dir.toUpperCase());
	}
	modelConfig.filter && filters.push(...modelConfig.filter); // adding the filters from the model config
	try {
		const modelSearchParams = evalAdjacentPayload({
			gridColumns,
			filterModel,
			standaloneFilters,
			standaloneFilterColumns,
			parentFilters
		});

		if (modelSearchParams) {
			const { filter: modelFilterSearchParam, ...otherModelSearchParams } = modelSearchParams;
			for (const [searchParamKey, searchParamValue] of Object.entries(otherModelSearchParams)) {
				const val =
					!!searchParamValue && typeof searchParamValue === 'object'
						? JSON.stringify(searchParamValue)
						: searchParamValue;
				if (formData.has(searchParamKey)) {
					formData.set(searchParamKey, val);
				} else {
					formData.append(searchParamKey, val);
				}
			}
			if (modelFilterSearchParam) {
				filters.push(...modelFilterSearchParam); // to re-contruct the filter like [{...},{...}],{...},...]
			}
		}
	} catch (error) {
		console.error('error', error);
	}
	if (lookups) {
		formData.append('lookups', JSON.stringify(lookups));
	}
	formData.append('action', 'export');
	formData.append('cols', JSON.stringify(cols));
	formData.append('filter', JSON.stringify(filters));
	formData.append('timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);
	formData.append('exportConfig', JSON.stringify(exportConfig || {}));
	formData.append('limit', limit);
	return await fetch(api, {
		method: 'POST',
		body: formData
	})
		.then((res) => res.blob())
		.then((blob) => {
			if (blob) {
				const href = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = href;
				link.setAttribute('download', `${fileName}${exportFileExtensions.get(exportFormat)}`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		})
		.finally(() => setIsLoading(false));
};

export const getList = async ({
	gridColumns,
	standaloneFilterColumns,
	setIsLoading,
	setData,
	page,
	pageSize,
	sortModel,
	filterModel,
	api,
	parentFilters,
	apiParams,
	standaloneFilters,
	modelConfig,
	backgroundApi
}) => {
	setIsLoading(true);
	const lookups = {};
	const dateFields = [];
	for (let { type, field } of gridColumns) {
		if (type === 'date') {
			dateFields.push(field);
		}
		continue;
	}
	const searchParams = new URLSearchParams();
	const lookupsToFetch = 'comboTypes' in modelConfig ? modelConfig.comboTypes : [];
	if (!('comboTypes' in modelConfig)) {
		modelConfig.columns.forEach((field) => {
			if (typeof field.lookup === 'string') {
				lookupsToFetch[field.lookup] = lookupsToFetch.push({ type: field.lookup, loaded: true });
			}
		});
	}
	if (filterModel) {
		filterModel.items = filterModel?.items?.map((item) => {
			if (item.value === null) {
				return {};
			}
			return item;
		});
	}
	searchParams.set('comboTypes', JSON.stringify(lookupsToFetch));
	searchParams.set('page', page + 1);
	searchParams.set('start', page * pageSize);
	searchParams.set('limit', pageSize);
	modelConfig.filter && searchParams.set('filter', JSON.stringify(modelConfig.filter));

	if (apiParams) {
		Object.keys(apiParams).forEach((key) => {
			searchParams.set(key, apiParams[key]);
		});
	}
	if (sortModel?.length) {
		const sort = sortModel.map((sort) => {
			return { sort: sort.field, dir: sort.sort };
		});
		if (singleSort) {
			searchParams.set('sort', sort[0].sort);
			searchParams.set('dir', sort[0].dir.toUpperCase());
			searchParams.set('sortDir', sort[0].dir.toUpperCase());
		} else {
			searchParams.set('sort', JSON.stringify(sort));
		}
	}
	try {
		const modelSearchParams = evalAdjacentPayload({
			gridColumns,
			filterModel,
			standaloneFilters,
			standaloneFilterColumns,
			parentFilters
		});
		if (modelSearchParams) {
			const { filter: modelFilterSearchParam, ...otherModelSearchParams } = modelSearchParams;
			for (const [searchParamKey, searchParamValue] of Object.entries(otherModelSearchParams)) {
				const val =
					!!searchParamValue && typeof searchParamValue === 'object'
						? JSON.stringify(searchParamValue)
						: searchParamValue;
				if (searchParams.has(searchParamKey)) {
					searchParams.set(searchParamKey, val);
				} else {
					searchParams.append(searchParamKey, val);
				}
			}
			if (modelFilterSearchParam) {
				searchParams.append('filter', JSON.stringify(modelFilterSearchParam));
			}
		}
	} catch (error) {
		console.error('error', error);
	}
	for (const key of searchParams.keys()) {
		const values = searchParams.getAll(key);
		let arrValues = [];
		if (values.length > 1) {
			for (const value of values) {
				try {
					let val = JSON.parse(value);
					if (Array.isArray(val)) {
						arrValues = arrValues.concat(val);
					}
				} catch (error) { /* empty */ }
			}
		}
		if (arrValues.length > 0) {
			searchParams.set(key, JSON.stringify(arrValues));
		}
	}
	if (lookups) {
		searchParams.set('lookups', JSON.stringify(lookups));
	}

	searchParams.set('action', 'list');
	searchParams.set('asArray', '0');
	const response = await fetch(api, {
		method: 'POST',
		headers: {
			'X-Background-Request': backgroundApi || modelConfig.backgroundApi || 'false'
		},
		body: searchParams
	});
	if (response.status === 200) {
		const data = await response.json();
		let { records: rows, recordCount: rowCount, combos, ...rest } = data;
		if (!Array.isArray(rows)) {
			rows = [];
		}


		setData({ rows, rowCount, combos, ...rest });
		setIsLoading(false);
		return true;
	} else {
		logger.error('Failed to load list', response.body);
		setIsLoading(false);
		return false;
	}
};

export const getRecord = async ({ api, id, setIsLoading, setActiveRecord, modelConfig, parentFilters, apiParams }) => {
	setIsLoading(true);
	const searchParams = new URLSearchParams();
	searchParams.set('action', 'load');
	searchParams.set('id', id === null ? 0 : id);

	if (apiParams) {
		Object.keys(apiParams).forEach((key) => {
			searchParams.set(key, apiParams[key]);
		});
	}

	const lookupsToFetch = 'recordCombosTypes' in modelConfig ? modelConfig.recordCombosTypes : 'comboTypes' in modelConfig ? modelConfig.comboTypes : [];
	if (!('comboTypes' in modelConfig)) {
		modelConfig.columns.forEach((field) => {
			if (typeof field.lookup === 'string') {
				lookupsToFetch[field.lookup] = lookupsToFetch.push({ type: field.lookup, loaded: true });
			}
		});
	}

	searchParams.set('comboTypes', JSON.stringify(lookupsToFetch));
	try {
		const response = await fetch(api, {
			method: 'POST',
			body: searchParams
		});
		if (response.status === 200) {
			const result = await response.json();
			const { data, combos, success } = result;

			if (!success) {
				logger.error('Load failed', result.info || result.err || 'Load failed');
				return false;
			}

			// Convert boolean values to boolean, dates to proper date objects
			const record = modelConfig.columns.reduce((acc, { field, type = 'string', multiple, formField }) => {
				if (multiple) {
					let v = acc[field];
					if (v && typeof v === 'string') {
						v = v.split(',');
					}
					acc[field] = v || [];
				}
				if (type === 'bool' || type === 'boolean') {
					acc[field] = [true, 1, 'true', 'yes'].includes(acc[field]);
				} else if (['date', 'dateTime', 'time'].includes(type)) {
					let fieldVal = acc[field];
					if (typeof fieldVal === 'string') {
						const dayjsDte = dayjs(util.fixDates(fieldVal)).utcOffset(0, true).utc();
						acc[field] = dayjsDte.isValid() ? dayjsDte : fieldVal;
					}
					if (typeof formField === 'string' && formField in acc) {
						let formFieldVal = acc[formField];
						if (typeof formFieldVal === 'string') {
							const dayjsDte = dayjs(util.fixDates(formFieldVal));
							acc[formField] = dayjsDte.isValid() ? dayjsDte : formFieldVal;
						}
					}
				}
				return acc
			}, data);

			setActiveRecord({
				id,
				title: record[modelConfig.formTitleField],
				record: { ...modelConfig.initialValues, ...parentFilters, ...record },
				combos
			});
			return true;
		}
		logger.error('Could not load record', new Error(response.body.toString()));
	} catch (error) {
		logger.error('Could not load record', error);
	} finally {
		setIsLoading(false);
	}
	return false;
};

export const deleteRecord = async function ({ id, api }) {
	if (!id) {
		logger.error('Deleted failed. No active record.');
		return;
	}
	try {
		const searchParams = new URLSearchParams();
		searchParams.set('action', 'delete');
		searchParams.set('ids', id);
		const response = await fetch(`${api}`, {
			method: 'POST',
			body: searchParams
		});
		const result = await response.json();
		if (result.err) {
			util.showError(strings[result.info], strings.Error, 'error')
		}
		if (response.status === 200) {
			return true;
		}
		logger.error('Deleted failed', response.body);
	} catch (error) {
		logger.error('Deleted failed', error);
	}
	return false;
};

export const saveRecord = async function ({
	id,
	api,
	values,
	isImport,
	action = 'save',
	parseOnSaveError,
	onSuccess: onSuccessCb,
	disableAlerts
}) {
	const saveDataObj = isImport
		? { ...values }
		: {
			action: action,
			...values,
			id:  id || 0
		};
	let method = 'POST';
	const formData = new FormData();
	formData.set('authToken', null);
	for (const [key, value] of Object.entries(saveDataObj)) {
		formData.set(key, value);
	}
	return await fetch(api, {
		method: method,
		body: formData
	})
		.then((res) => res.json())
		.then((json) => {
			const { success, data } = { ...json };
			if ((success || !!data)) {
				if (!disableAlerts) {
					onSuccess(json);
				}

				return json;
			} else {
				throw json;
			}
		})
		.catch((err) => {
			if (typeof parseOnSaveError === 'function') {
				const parsedError = parseOnSaveError(err, {
					id,
					api,
					values,
					isImport,
					action,
					onSuccess: onSuccessCb
				});
				err = { ...err, ...parsedError };
			}

			onError(err);
		});
};

export const updateOrdering = async function ({ api, data, setIsLoading }) {
	const setLoading = (loading) => {
		if (typeof setIsLoading === 'function') {
			setIsLoading(loading);
		}
	};
	setLoading(true);
	return fetch(api, {
		method: 'POST',
		body: new URLSearchParams({
			action: 'otherAction',
			otherAction: 'UpdateOrdering',
			...data
		})
	})
		.finally(() => setLoading(false));
};

export const printFetch = (api, formValue) => {
	return fetch(api, { method: 'Post', credentials: 'include', body: formValue })
}
